import React from 'react';
import * as LucideIcons from 'lucide-react';

type IconName = keyof typeof LucideIcons;

interface IconProps {
  name: string;
  size?: number;
  className?: string;
}

/**
 * Icon component that maps string names to Lucide icons
 * 
 * @param {string} name - The name of the icon to render
 * @param {number} size - Optional size for the icon
 * @param {string} className - Optional CSS class to apply to the icon
 * @returns React component
 */
const Icon: React.FC<IconProps> = ({ name, size = 24, className = "h-6 w-6 text-purple-600" }) => {
  // Convert kabab-case or snake_case to PascalCase
  const formatIconName = (iconName: string): IconName => {
    // Handle special cases for icon names that don't match Lucide's naming convention
    const specialCases: Record<string, string> = {
      'chart': 'BarChart4',
      'message': 'MessageSquare',
      'userCog': 'UserCog'
    };
    
    if (specialCases[iconName]) {
      return specialCases[iconName] as IconName;
    }
    
    return iconName
      .split(/[-_]/)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join('') as IconName;
  };
  
  const iconName = formatIconName(name);
  const LucideIcon = LucideIcons[iconName] as React.FC<{ size?: number; className?: string }>;
  
  if (!LucideIcon) {
    
    return null;
  }
  
  return <LucideIcon size={size} className={className} />;
};

export default Icon;
